import { computed, ref } from "vue";
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";

import LocalForagePlugin, { sectors } from "@/plugins/LocalForagePlugin";
import { InvestorsIntelligenceSector, SectorMarketState } from "@/types/entities";

import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";

import type { DataSeries, SectorMetrics } from "@/types/api/sector-rs-matrix";
import { refreshSectorsFromServer } from "@/services/data/sync/sector-sync-coordinator";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

const initialized = computed< boolean >( () => allSectors.value.length > 0 );
const isLoading = computed< boolean >( () => loading.value );
const loading = ref< boolean >( false );
const contentfulRefreshing = ref< boolean >( false );
const iiRefreshing = ref< boolean >( false );

const isRelativeStrengthLoading = computed< boolean >( () => relativeStrengthLoading.value );
const relativeStrengthLoading = ref< boolean >( false );

const defaultSectors = [] as unknown as InvestorsIntelligenceSector[];
const allSectors = ref< InvestorsIntelligenceSector[] >( defaultSectors );

const defaultRelativeStrengthMatrix = [] as unknown as SectorMetrics[];
const matrix = ref< SectorMetrics[] >( defaultRelativeStrengthMatrix );

const defaultRelativeStrengthHistory = [] as unknown as DataSeries[];
const rsHistory = ref< DataSeries[] >( defaultRelativeStrengthHistory );

const selectedSectorDatePoint = ref< SectorMarketState | undefined >();

export const useSectorsStore = defineStore( 'sectors', () => {
    const { authToken } = storeToRefs( useAuthStore() );
    const userStore = useUserStore();

    function resetSectors(): void {
        allSectors.value.slice( 0 );
    }

    function resetRelativeStrength(): void {
        matrix.value = defaultRelativeStrengthMatrix;
        rsHistory.value = defaultRelativeStrengthHistory;
        relativeStrengthLoading.value = false;
    }

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        resetSectors();
        resetRelativeStrength();

        loading.value = false;
        contentfulRefreshing.value = false;
        iiRefreshing.value = false;
    }

    async function initializeAsync(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Sectors ].initialize() exiting - already processing...' );
            return;
        }

        loading.value = true;

        const t0: number = performance.now();

        if ( typeof sectors === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await sectors.ready();
        if ( await sectors.length() > 0 ) {
            const uniqueSectors = new Map< number, InvestorsIntelligenceSector >();
            // Clear the contents of the searchable collections
            allSectors.value.splice( 0 );

            // Iterate over all elements within IDB
            sectors.iterate( ( sector: InvestorsIntelligenceSector ) => {
                uniqueSectors.set( sector.id, sector );
                return;
            } ).then( () => {
                allSectors.value = [ ...( Array.from( uniqueSectors.values() ) ) ]
                    .sort( ( s1: InvestorsIntelligenceSector, s2: InvestorsIntelligenceSector ) =>
                        s1.name.toLowerCase().localeCompare( s2.name.toLowerCase() )
                    );

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allSectors.value.length } sectors in ${ t1 - t0 } ms` );
            }).catch( ( err: any ) => {
                logger.error( JSON.stringify( err.message ) );
                reset();
            } ).finally( () => {
                // Clear the "loading" flag
                loading.value = false;
            } );
        } else {
            if ( contentfulRefreshing.value ) {
                return;
            }

            contentfulRefreshing.value = true;

            // Timing - testing to see how expensive each method is
            const t0: number = performance.now();

            refreshSectorsFromServer( authToken.value )
                .then( ( hydratedSectors: InvestorsIntelligenceSector[] ) => allSectors.value = [ ...hydratedSectors ] )
                .catch( ( err: any ) => {
                    logger.error( JSON.stringify( err.message ) );
                    reset();
                } )
                .finally( () => {
                    // Clear the "loading" flag
                    loading.value = false;
                    contentfulRefreshing.value = false;

                    const t1: number = performance.now();
                    logger.info( `Fetching & mapping ${ allSectors.value.length } tmi/ii sector items took ${ t1 - t0 } ms` );
                } );
        }
    }

    const favoriteSectors = computed< InvestorsIntelligenceSector[] >( () => {
        return allSectors.value.filter( ( sector: InvestorsIntelligenceSector ) => userStore.isFavoriteSector( sector.id ) );
    } );

    const selectedSector = computed< SectorMarketState | undefined >( () => selectedSectorDatePoint.value );

    function sectorBySymbol( symbol: string ): InvestorsIntelligenceSector | undefined {
        return allSectors.value.find( ( sector: InvestorsIntelligenceSector ): boolean => sector.symbol === symbol );
    }

    function sectorByTmiId( id: number ): InvestorsIntelligenceSector | undefined {
        return allSectors.value.find( ( sector: InvestorsIntelligenceSector ): boolean => sector.id === id );
    }

    function sectorById( id: string ): InvestorsIntelligenceSector | undefined {
        return allSectors.value.find( ( sector: InvestorsIntelligenceSector ): boolean => sector.contentfulId === id );
    }

    function setSelectedSector( sectorHistory: SectorMarketState ) {
        selectedSectorDatePoint.value = sectorHistory;
    }

/*
    function fetchRelativeStrengthMatrix( dt?: string ): void {
        const {
            onResult,
            onError
        } = useQuery< SectorRelativeStrengthMatrix >( rsMatrixQuery, null,
                {
                    clientId: marketDataServiceKey,
                    debounce: 2000,
                    prefetch: false,
                    throttle: 500
                } );

        onResult( ( result: ApolloQueryResult< SectorRelativeStrengthMatrix > ) => {
            if ( result.loading ) {
                return;
            }

            const response: SectorRelativeStrengthMatrix = result.data;
            if ( null != response?.sectorRelativeStrengthMatrix?.matrix ) {
                relativeStrengthLoading.value = false;

                matrix.value = structuredClone( response.sectorRelativeStrengthMatrix.matrix );
                matrix.value.sort( ( s1: SectorMetrics, s2: SectorMetrics ) => s1.rank - s2.rank );

                const rankingMap = new Map< number, number >();
                matrix.value.map( ( item: SectorMetrics ) => {
                    rankingMap.set( item.id, item.rank );
                });

                matrix.value = matrix.value.map( ( s: SectorMetrics ) => {
                    // Provide a self-reference (null comparison)
                    s.values.push( { id: s.id, sig: null, dir: null } );
                    s.values.sort(( rs1: RelativeSector, rs2: RelativeSector ) => rankingMap.get( rs2.id )! - rankingMap.get( rs1.id )! );

                    return s;
                } );
            }
        } );

        onError( ( err: ApolloError ) => {
            // TODO: Send error to ux-svc
            logger.error( err );
            resetRelativeStrength();
        } );
    }

    function fetchRelativeStrengthHistoryForSector( id: number ): void {
        relativeStrengthLoading.value = true;
        const {
            onResult,
            onError
        } = useQuery< RelativeStrengthHistory >(
                rsHistoryQuery,
                {
                    sectorId: id
                },
                {
                    clientId: marketDataServiceKey,
                    fetchPolicy: 'cache-and-network'
                } );

        onResult( ( result: ApolloQueryResult< RelativeStrengthHistory > ) => {
            const response: RelativeStrengthHistory = result.data;
            if ( null != response?.history ) {
                relativeStrengthLoading.value = false;

                rsHistory.value = [ ...response?.history ];
            }
        } );

        onError( ( err: ApolloError ) => {
            // TODO: Send error to ux-svc
            logger.error( err );
            resetRelativeStrength();
        } );
    }
*/

    return {
        // ACTIONS
        // fetchRelativeStrengthHistoryForSector,
        // fetchRelativeStrengthMatrix,
        initializeAsync,
        reset,
        sectorById,
        sectorByTmiId,
        sectorBySymbol,
        setSelectedSector,

        // GETTERS
        allSectors,
        favoriteSectors,
        isLoading,
        isRelativeStrengthLoading,
        matrix,
        rsHistory,
        selectedSector
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSectorsStore, import.meta.hot ) );
}
